/**
 * @CODE:ACC-003-AMENITIES-001
 * AmenitiesGrid Component
 *
 * Display amenities in a grid layout:
 * - Shows 7 main amenities with icons
 * - "Show All" button for remaining amenities
 * - Dialog displays all amenities in grid
 * - Icon mapping for common amenities
 * - Responsive responsive layout
 * - Handles JSONB amenities data
 */

import { useState } from "react";
import {
  Wifi,
  ParkingCircle,
  Wind,
  WashingMachine,
  Tv,
  Wind as Refrigerator,
  Droplets,
  UtensilsCrossed,
  Dumbbell,
  Coffee,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface AmenitiesGridProps {
  /** Array of amenity strings */
  amenities: string[];
  /** Optional CSS class */
  className?: string;
}

/**
 * Map amenity name to Lucide icon
 */
function getAmenityIcon(amenity: string): React.ComponentType<{
  className?: string;
}> | null {
  const normalizedName = amenity.toLowerCase();

  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    wifi: Wifi,
    "와이파이": Wifi,
    parking: ParkingCircle,
    "주차": ParkingCircle,
    "에어컨": Wind,
    "ac": Wind,
    "washing": WashingMachine,
    "세탁기": WashingMachine,
    tv: Tv,
    "텔레비전": Tv,
    "냉장고": Refrigerator,
    "fridge": Refrigerator,
    "욕실용품": Droplets,
    "bathroom": Droplets,
    "kitchen": UtensilsCrossed,
    "키친": UtensilsCrossed,
    "헬스장": Dumbbell,
    "gym": Dumbbell,
    "카페": Coffee,
    "cafe": Coffee,
  };

  // Try to find a match in the map
  for (const [key, icon] of Object.entries(iconMap)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return icon;
    }
  }

  return null;
}

/**
 * Amenity item component with icon and text
 */
function AmenityItem({
  amenity,
  className,
}: {
  amenity: string;
  className?: string;
}): JSX.Element {
  const Icon = getAmenityIcon(amenity);

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors",
        className
      )}
    >
      {Icon ? (
        <Icon className="h-6 w-6 text-gray-700" />
      ) : (
        <div className="h-6 w-6 bg-gray-300 rounded"></div>
      )}
      <p className="text-xs text-center text-gray-700 font-medium">
        {amenity}
      </p>
    </div>
  );
}

/**
 * Grid component displaying accommodation amenities
 * Shows 7 main amenities with option to view all in dialog
 */
export const AmenitiesGrid = ({
  amenities,
  className,
}: AmenitiesGridProps): JSX.Element => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!amenities || amenities.length === 0) {
    return (
      <div className={cn("w-full", className)}>
        <p className="text-sm text-gray-500">편의시설 정보가 없습니다</p>
      </div>
    );
  }

  const displayedAmenities = amenities.slice(0, 7);
  const hasMoreAmenities = amenities.length > 7;
  const remainingCount = amenities.length - 7;

  return (
    <div className={cn("w-full", className)}>
      {/* Main Grid - 7 items */}
      <div
        className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3"
        role="region"
        aria-label="숙박시설 편의시설"
      >
        {displayedAmenities.map((amenity) => (
          <AmenityItem key={amenity} amenity={amenity} />
        ))}

        {/* Show All Button */}
        {hasMoreAmenities && (
          <button
            onClick={() => setIsDialogOpen(true)}
            className="flex flex-col items-center justify-center gap-2 p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors border border-gray-300"
            aria-label={`모든 편의시설 보기 (${remainingCount}개 더)`}
          >
            <ChevronRight className="h-6 w-6 text-gray-600" />
            <p className="text-xs text-center text-gray-600 font-medium">
              {remainingCount}개<br />
              더보기
            </p>
          </button>
        )}
      </div>

      {/* All Amenities Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-screen overflow-auto">
          <DialogHeader>
            <DialogTitle>전체 편의시설</DialogTitle>
            <DialogDescription>
              {amenities.length}개의 편의시설을 제공합니다
            </DialogDescription>
          </DialogHeader>

          {/* Full Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mt-6">
            {amenities.map((amenity) => (
              <AmenityItem key={amenity} amenity={amenity} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
